import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { Cue } from '@core/model/cue.ts';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import {
  clampCuePanelHeight,
  clampCuePanelWidth,
  DEFAULT_CUE_PANEL_SIZE,
  readCuePanelSize,
  writeCuePanelSize,
  type CuePanelSize,
} from '../lib/cuePanelSize.ts';
import type { SubtitleStore, SubtitleSlot } from '../state/subtitleStore.ts';
import { useI18n } from '../i18n/index.ts';
import { SheetHandle } from './SheetHandle.tsx';

export type CuePanelProps = {
  open: boolean;
  onClose: () => void;
  store: SubtitleStore;
  settingsStore: SettingsStore;
  video: VideoController | null;
};

/** Same clock format as extension panelController.formatCueTime. */
export function formatCueTime(ms: number): string {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Strip tags but keep newlines (extension panel uses pre-line dual lines). */
export function stripHtmlPreserveLines(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/\n[^\S\n]+/g, '\n')
    .replace(/[^\S\n]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Extension panel rows: source text + optional translation line (always, when present).
 * Dictation / speaking adjust the primary line only.
 */
export function cueListParts(
  cue: Cue,
  options: { speakingMode: boolean; loadingText: string },
): { primary: string; translation?: string } {
  if (cue.loading) return { primary: options.loadingText };

  let primary: string;
  if (cue.dictation && !options.speakingMode) {
    primary = cue.dictation;
  } else {
    primary = cue.kuroshiro ?? cue.text;
  }

  const translation =
    cue.translation && cue.translation.trim()
      ? stripHtmlPreserveLines(cue.translation)
      : undefined;

  return {
    primary: stripHtmlPreserveLines(primary),
    translation,
  };
}

/**
 * Center a cue row in the list viewport by adjusting `scrollTop`
 * (extension VirtualScroller.adjustToAlign / goToIndex 'center').
 */
export function scrollCueRowToCenter(container: HTMLElement, row: HTMLElement): boolean {
  const containerRect = container.getBoundingClientRect();
  const itemRect = row.getBoundingClientRect();
  const containerCenter = container.clientHeight / 2;
  const itemCenter = itemRect.top - containerRect.top + itemRect.height / 2;
  const delta = itemCenter - containerCenter;
  if (Math.abs(delta) <= 1) return false;
  const max = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTop = Math.max(0, Math.min(max, container.scrollTop + delta));
  return true;
}

/**
 * Scrollable cue list aligned with extension panelController:
 * seek+play, follow-center, clear highlight when paused, dual translation lines.
 */
export function CuePanel({ open, onClose, store, settingsStore, video }: CuePanelProps) {
  const { t } = useI18n();
  const [slot, setSlot] = useState<SubtitleSlot>('main');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  /** When locked, list auto-scrolls to the active cue (extension panel parity). */
  const [locked, setLocked] = useState(true);
  const [size, setSize] = useState<CuePanelSize>(() => {
    try {
      if (typeof localStorage === 'undefined') return { ...DEFAULT_CUE_PANEL_SIZE };
      return readCuePanelSize(localStorage);
    } catch {
      return { ...DEFAULT_CUE_PANEL_SIZE };
    }
  });
  const listRef = useRef<HTMLDivElement>(null);
  /** Ignore unlock while we programmatically move scrollTop (extension skipScrollHandler). */
  const programmaticScrollRef = useRef(false);
  const dragRef = useRef<{
    axis: 'width' | 'height';
    start: number;
    origin: number;
    pointerId: number;
  } | null>(null);

  const settings = useSyncExternalStore(
    (cb) => settingsStore.subscribe(cb),
    () => settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS,
  );
  const mainSlot = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('main'),
  );
  const secondSlot = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('second'),
  );
  const slotState = slot === 'main' ? mainSlot : secondSlot;
  const cues = slotState.cues ?? [];

  useEffect(() => {
    if (!open || !video || !cues.length) {
      setActiveIndex(null);
      return;
    }

    let raf = 0;
    const tick = () => {
      // Extension: clear highlight while paused.
      if (video.isPaused()) {
        setActiveIndex(null);
      } else {
        const delayMs = slotState.view.delayMs;
        const timeMs = video.getCurrentTimeMs() + delayMs;
        const idx = cues.findIndex((c) => c.start <= timeMs && timeMs <= c.end);
        setActiveIndex(idx >= 0 ? idx : null);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, video, cues, slotState.view.delayMs]);

  useEffect(() => {
    if (!locked || activeIndex === null || !listRef.current) return;
    const list = listRef.current;
    const row = list.querySelector<HTMLElement>(`[data-cue-index="${activeIndex}"]`);
    if (!row) return;

    programmaticScrollRef.current = true;
    scrollCueRowToCenter(list, row);
    // Second pass after layout (extension adjustWithRetry).
    requestAnimationFrame(() => {
      scrollCueRowToCenter(list, row);
      requestAnimationFrame(() => {
        programmaticScrollRef.current = false;
      });
    });
  }, [activeIndex, locked]);

  const onSelectCue = useCallback(
    (cue: Cue) => {
      if (!video) return;
      video.seekMs(cue.start);
      video.play();
    },
    [video],
  );

  const onListScroll = useCallback(() => {
    // User drag/wheel only — programmatic follow must keep the scrollbar locked.
    if (programmaticScrollRef.current) return;
    if (locked) setLocked(false);
  }, [locked]);

  const persistSize = useCallback((next: CuePanelSize) => {
    setSize(next);
    try {
      writeCuePanelSize(next);
    } catch {
      // ignore
    }
  }, []);

  const onResizeStart = (axis: 'width' | 'height') => (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragRef.current = {
      axis,
      start: axis === 'width' ? event.clientX : event.clientY,
      origin: axis === 'width' ? size.widthPx : size.heightPx,
      pointerId: event.pointerId,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onResizeMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.axis === 'width') {
      const delta = drag.start - event.clientX;
      persistSize({ ...size, widthPx: clampCuePanelWidth(drag.origin + delta) });
    } else {
      const delta = drag.start - event.clientY;
      persistSize({ ...size, heightPx: clampCuePanelHeight(drag.origin + delta) });
    }
  };

  const onResizeEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  if (!open) return null;

  const panelStyle: CSSProperties = {
    width: size.widthPx,
    maxHeight: size.heightPx,
  };

  return (
    <aside
      className="cue-panel panel-sheet"
      data-testid="cue-panel"
      aria-label={t('cuePanelTitle')}
      style={panelStyle}
    >
      <SheetHandle onDismiss={onClose} />
      <button
        type="button"
        className="cue-panel__resize cue-panel__resize--width"
        data-testid="cue-panel-resize-width"
        aria-label={t('cuePanelResize')}
        onPointerDown={onResizeStart('width')}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
      />
      <button
        type="button"
        className="cue-panel__resize cue-panel__resize--height"
        data-testid="cue-panel-resize-height"
        aria-label={t('cuePanelResize')}
        onPointerDown={onResizeStart('height')}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
      />
      <div className="cue-panel__header">
        <h2>{t('cuePanelTitle')}</h2>
        <button type="button" className="cue-panel__close" onClick={onClose} aria-label={t('settingsClose')}>
          {t('settingsClose')}
        </button>
      </div>
      <div className="cue-panel__toolbar">
        <select
          className="cue-panel__slot-select"
          data-testid="cue-panel-slot"
          aria-label={t('cuePanelSlot')}
          value={slot}
          onChange={(e) => setSlot(e.target.value as SubtitleSlot)}
        >
          <option value="main">{t('cuePanelMain')}</option>
          <option value="second">{t('cuePanelSecond')}</option>
        </select>
        <button
          type="button"
          className={`cue-panel__lock${locked ? ' cue-panel__lock--on' : ''}`}
          data-testid="cue-panel-lock"
          aria-pressed={locked}
          onClick={() => setLocked((v) => !v)}
        >
          {locked ? t('cuePanelLock') : t('cuePanelUnlock')}
        </button>
      </div>
      <div
        ref={listRef}
        className="cue-panel__list"
        data-testid="cue-panel-list"
        onScroll={onListScroll}
      >
        {cues.length === 0 ? (
          <p className="cue-panel__empty">{t('cuePanelEmpty')}</p>
        ) : (
          cues.map((cue, index) => {
            const parts = cueListParts(cue, {
              speakingMode: settings.speakingMode,
              loadingText: t('serviceLoading'),
            });
            return (
              <button
                key={`${cue.start}-${cue.end}-${index}`}
                type="button"
                className={`cue-panel__row${activeIndex === index ? ' cue-panel__row--active' : ''}`}
                data-testid={`cue-row-${index}`}
                data-cue-index={index}
                onClick={() => onSelectCue(cue)}
              >
                <span className="cue-panel__body">
                  <span className="cue-panel__time">{formatCueTime(cue.start)}</span>
                  <span className="cue-panel__text">{parts.primary}</span>
                  {parts.translation ? (
                    <span className="cue-panel__translation">{parts.translation}</span>
                  ) : null}
                </span>
                <span className="cue-panel__play" aria-hidden="true">
                  ▶
                </span>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
